/**
 * RachioScheduleBuilder — full Rachio-mirror schedule manager inside TurfPulse.
 *
 * Replaces both the legacy "My Sprinkler Schedule" zone log AND the read-only
 * RecurringSmartSchedulesCard. This single component is the front door for:
 *   - Listing all server-side schedules (db.irrigation_schedules)
 *   - Creating new schedules with the same dials Rachio's app exposes:
 *       name, days, start time, multi-zone with per-zone duration,
 *       cycle-and-soak per zone, rain-skip threshold, smart-gap split
 *   - Editing existing schedules (same modal, pre-filled)
 *   - Run-now (fires schedule instantly via /run-now → start_multiple)
 *   - Skip-next (advances next_fire_at by one occurrence)
 *   - Pause/resume (toggle enabled)
 *   - Delete
 *
 * The "Run my schedule" button on the QuickTestRunCard also pulls from this
 * same data source — single source of truth.
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Play, Square, SkipForward, Pencil, Plus, Loader2, Calendar, Clock, Droplets } from 'lucide-react';
import { API_URL } from '../config/api';
import { MadSourcePill } from './MadSourcePill';

const DOW = [
  { idx: 0, key: 'M', label: 'Mon' },
  { idx: 1, key: 'T', label: 'Tue' },
  { idx: 2, key: 'W', label: 'Wed' },
  { idx: 3, key: 'T', label: 'Thu' },
  { idx: 4, key: 'F', label: 'Fri' },
  { idx: 5, key: 'S', label: 'Sat' },
  { idx: 6, key: 'S', label: 'Sun' },
];

// Compute per-day schedule "slips" — mirrors backend `compute_day_multiplier`
// and `_build_zone_payload` so the UI shows EXACTLY what TurfPulse will fire
// at Rachio on each day. Returns ordered array of:
//   { dow, day_label, gap_after, multiplier, total_minutes, zones: [...] }
// where each zone slip is:
//   { name, rachio_zone_id, cycle_count, per_cycle_min, total_min,
//     entered_per_cycle, soak_minutes }
const computePerDaySchedule = (daysOfWeek, zones, strategy = 'smart_gap') => {
  if (!daysOfWeek?.length || !zones?.length) return [];
  const sortedDays = [...new Set(daysOfWeek)].sort((a, b) => a - b);
  const N = sortedDays.length;
  return sortedDays.map((d, i) => {
    const next = sortedDays[(i + 1) % N];
    const gap = i === N - 1 ? 7 - d + next : next - d;
    const mult = (strategy === 'smart_gap' && N > 1) ? (gap / 7.0) * N : 1.0;
    const zoneSlips = zones.map((z) => {
      const cc = Math.max(1, Number(z.cycle_count) || 1);
      const dm = Number(z.duration_minutes) || 0;
      // Excluded zones (duration_minutes == 0) stay 0 — never floor up to 1.
      const isExcluded = dm === 0;
      const enteredPerCycle = isExcluded
        ? 0
        : (z.cycle_minutes ? Number(z.cycle_minutes) : Math.max(1, Math.floor(dm / cc)));
      const adjPerCycle = isExcluded ? 0 : Math.max(1, Math.round(enteredPerCycle * mult));
      const totalMin = adjPerCycle * cc;
      const soak = Number(z.soak_minutes) || 0;
      return {
        name: z.name || `Zone ${(z.rachio_zone_id || '').slice(-4) || '?'}`,
        rachio_zone_id: z.rachio_zone_id,
        cycle_count: cc,
        per_cycle_min: adjPerCycle,
        total_min: totalMin,
        entered_per_cycle: enteredPerCycle,
        soak_minutes: soak,
      };
    });
    const totalDayMin = zoneSlips.reduce((s, z) => s + z.total_min, 0);
    const nextDayLabel = DOW.find((x) => x.idx === next)?.label || '?';
    return {
      dow: d,
      day_label: DOW.find((x) => x.idx === d)?.label || '?',
      next_day_label: nextDayLabel,
      gap_after: gap,
      multiplier: mult,
      total_minutes: totalDayMin,
      zones: zoneSlips,
    };
  });
};

// Per-day schedule "slip" renderer — shows each day as its own card with
// zones in run order, exact minutes, and cycle-and-soak structure. Used in
// both the schedule list (compact) and the editor preview (full).
const PerDayScheduleSlips = ({ slips, compact = false, testIdPrefix = 'slip' }) => {
  if (!slips?.length) return null;
  const isSplit = slips.some((s) => Math.abs(s.multiplier - 1.0) > 0.01);
  return (
    <div className={`grid gap-2 ${slips.length > 2 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2'}`} data-testid={`${testIdPrefix}-grid`}>
      {slips.map((slip) => {
        const pct = Math.round((slip.multiplier - 1) * 100);
        const tone =
          pct > 0 ? 'border-emerald-500/50 bg-emerald-500/5'
          : pct < 0 ? 'border-amber-500/40 bg-amber-500/5'
          : 'border-border bg-background/40';
        const pctLabel = pct === 0 ? '' : (pct > 0 ? `+${pct}%` : `${pct}%`);
        return (
          <div
            key={slip.dow}
            className={`rounded-lg border p-2.5 ${tone}`}
            data-testid={`${testIdPrefix}-day-${slip.dow}`}
          >
            <div className="flex items-baseline justify-between gap-2 border-b border-border/50 pb-1.5 mb-1.5">
              <div className="flex items-baseline gap-1.5">
                <span className="font-bold text-sm tracking-wide uppercase">{slip.day_label}</span>
                <span className="text-[10px] text-muted-foreground">→ {slip.next_day_label} ({slip.gap_after}d gap)</span>
              </div>
              <div className="text-right">
                <div className="font-mono tabular-nums text-base font-bold leading-none" data-testid={`${testIdPrefix}-total-${slip.dow}`}>
                  {slip.total_minutes} min
                </div>
                {isSplit && pctLabel && (
                  <div className={`text-[9px] font-semibold leading-tight ${pct > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'}`}>
                    {pctLabel} · ×{slip.multiplier.toFixed(2)}
                  </div>
                )}
              </div>
            </div>
            <ol className="space-y-1">
              {slip.zones.map((z, idx) => (
                <li
                  key={idx}
                  className="flex items-baseline justify-between gap-2 text-[11px]"
                  data-testid={`${testIdPrefix}-day-${slip.dow}-zone-${idx}`}
                >
                  <span className="flex-1 min-w-0 truncate">
                    <span className="text-muted-foreground/70 mr-1">{idx + 1}.</span>
                    <span className="text-foreground">{z.name}</span>
                    {!compact && z.cycle_count > 1 && (
                      <span className="text-muted-foreground ml-1 text-[10px]">
                        · {z.cycle_count} × {z.per_cycle_min} min{z.soak_minutes ? ` · ${z.soak_minutes}m soak` : ' · soak via round-robin'}
                      </span>
                    )}
                  </span>
                  <span className="font-mono tabular-nums shrink-0 font-semibold">{z.total_min} min</span>
                </li>
              ))}
            </ol>
          </div>
        );
      })}
    </div>
  );
};

const _rachioHeaders = () => {
  const tok = (typeof window !== 'undefined') ? localStorage.getItem('tp_rachio_token') : null;
  return tok ? { 'X-Rachio-Token': tok } : {};
};

const emptySchedule = () => ({
  name: 'My Schedule',
  days_of_week: [1, 4],     // Tue + Thu default
  start_time_local: '06:00',
  timezone: 'America/Chicago',
  enabled: true,
  zones: [],
  smart_skip: {
    skip_if_rain_forecast_in: 0.25,
    skip_if_in_blackout: true,
    skip_if_soil_moisture_above_pct: null,
    skip_if_freeze_below_f: null,        // off by default
    skip_if_wind_above_mph: null,
    skip_if_mad_below_pct: null,
    skip_if_dewpoint_above_f: null,
    skip_if_forecast_intelligence_says_skip: false,  // Rule 13
  },
  split_strategy: 'even',
  probe_driven_tuning: false,
  adaptive_runtime: false,           // Decision Engine v2.0
  time_aware_optimization: false,    // Closed-loop Phase 2
});

export const RachioScheduleBuilder = () => {
  const [schedules, setSchedules] = useState([]);
  const [rachioZones, setRachioZones] = useState([]);
  const [rachioConnected, setRachioConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);  // null = new
  const [draft, setDraft] = useState(emptySchedule());
  const [saving, setSaving] = useState(false);

  // Activity log (recent fires from cron, run-now, and Rachio webhooks)
  const [activity, setActivity] = useState([]);
  const [byZone, setByZone] = useState([]);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [expandedRun, setExpandedRun] = useState(null);   // index of expanded row

  // ── Load schedules + Rachio zones ────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const [sch, zr, act, bz] = await Promise.all([
        axios.get(`${API_URL}/api/irrigation/schedules`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rachio/zones`, { withCredentials: true, headers: _rachioHeaders() }),
        axios.get(`${API_URL}/api/irrigation/schedules/runs/activity`, { withCredentials: true }).catch(() => ({ data: { runs: [] } })),
        axios.get(`${API_URL}/api/irrigation/schedules/runs/by-zone`, { withCredentials: true }).catch(() => ({ data: { zones: [] } })),
      ]);
      setSchedules(sch.data?.schedules || sch.data || []);
      const dev = zr.data?.device || {};
      const zs = Array.isArray(dev.zones) ? dev.zones : [];
      zs.sort((a, b) => (a.zone_number || 0) - (b.zone_number || 0));
      setRachioZones(zs);
      setRachioConnected(zr.data?.mode === 'live');
      setActivity(act.data?.runs || []);
      setByZone(bz.data?.zones || []);
      setActivityLoaded(true);
    } catch (e) {
      // 401 silently ignored — page protected by ProtectedRoute
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Actions ──────────────────────────────────────────────────────
  const setBusyFor = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const runNow = async (s) => {
    if (!window.confirm(`Run "${s.name}" now? Rachio will fire ${s.zones?.length || 0} zone${s.zones?.length !== 1 ? 's' : ''}.`)) return;
    setBusyFor(s.id, true);
    try {
      const r = await axios.post(
        `${API_URL}/api/irrigation/schedules/${s.id}/run-now`,
        {},
        { withCredentials: true, headers: _rachioHeaders() },
      );
      toast.success(`▶ ${s.name} running — ${r.data.zones_count} zone${r.data.zones_count !== 1 ? 's' : ''}, ~${r.data.total_minutes} min total.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Run-now failed');
    } finally {
      setBusyFor(s.id, false);
    }
  };

  const skipNext = async (s) => {
    if (!window.confirm(`Skip the next run of "${s.name}"?`)) return;
    setBusyFor(s.id, true);
    try {
      await axios.post(
        `${API_URL}/api/irrigation/schedules/${s.id}/skip-next`,
        {},
        { withCredentials: true },
      );
      toast.success('Next run skipped.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Skip failed');
    } finally {
      setBusyFor(s.id, false);
    }
  };

  const togglePause = async (s) => {
    setBusyFor(s.id, true);
    try {
      const r = await axios.post(
        `${API_URL}/api/irrigation/schedules/${s.id}/toggle`,
        {},
        { withCredentials: true },
      );
      toast.success(r.data.enabled ? 'Schedule resumed.' : 'Schedule paused.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusyFor(s.id, false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? This cannot be undone.`)) return;
    setBusyFor(s.id, true);
    try {
      await axios.delete(`${API_URL}/api/irrigation/schedules/${s.id}`, { withCredentials: true });
      toast.success('Schedule deleted.');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyFor(s.id, false);
    }
  };

  // ── Editor ───────────────────────────────────────────────────────
  const openNew = () => {
    setEditingId(null);
    const d = emptySchedule();
    // Pre-populate with all Rachio zones at 10 min each so the user has a starting point
    if (rachioZones.length) {
      d.zones = rachioZones.map((z, i) => ({
        rachio_zone_id: z.id,
        duration_minutes: 10,
        sort_order: i + 1,
        name: z.name,
        cycle_count: 1,
        cycle_minutes: null,
        soak_minutes: 0,
      }));
    }
    setDraft(d);
    setEditorOpen(true);
  };

  const openEdit = (s) => {
    setEditingId(s.id);
    setDraft({
      name: s.name,
      days_of_week: s.days_of_week || [],
      start_time_local: s.start_time_local || '06:00',
      timezone: s.timezone || 'America/Chicago',
      enabled: s.enabled !== false,
      zones: (s.zones || []).map((z) => ({
        rachio_zone_id: z.rachio_zone_id,
        duration_minutes: z.duration_minutes,
        sort_order: z.sort_order || 1,
        name: z.name || rachioZones.find((rz) => rz.id === z.rachio_zone_id)?.name || 'Zone',
        cycle_count: z.cycle_count || 1,
        cycle_minutes: z.cycle_minutes ?? null,
        soak_minutes: z.soak_minutes || 0,
        soil_type: z.soil_type ?? null,
        slope: z.slope ?? null,
        nozzle_inhr: z.nozzle_inhr ?? null,
        root_depth_in: z.root_depth_in ?? null,
        area_sqft: z.area_sqft ?? null,
        sun_exposure: z.sun_exposure ?? null,
        skip_if_zone_moisture_above_pct: z.skip_if_zone_moisture_above_pct ?? null,
        _showAdvanced: false,
      })),
      smart_skip: {
        skip_if_rain_forecast_in: s.smart_skip?.skip_if_rain_forecast_in ?? 0.25,
        skip_if_in_blackout: s.smart_skip?.skip_if_in_blackout ?? true,
        skip_if_soil_moisture_above_pct: s.smart_skip?.skip_if_soil_moisture_above_pct ?? null,
        skip_if_freeze_below_f: s.smart_skip?.skip_if_freeze_below_f ?? null,
        skip_if_wind_above_mph: s.smart_skip?.skip_if_wind_above_mph ?? null,
        skip_if_mad_below_pct: s.smart_skip?.skip_if_mad_below_pct ?? null,
        skip_if_dewpoint_above_f: s.smart_skip?.skip_if_dewpoint_above_f ?? null,
        skip_if_forecast_intelligence_says_skip: !!s.smart_skip?.skip_if_forecast_intelligence_says_skip,
      },
      split_strategy: s.split_strategy || 'even',
      probe_driven_tuning: !!s.probe_driven_tuning,
      adaptive_runtime: !!s.adaptive_runtime,
      time_aware_optimization: !!s.time_aware_optimization,
    });
    setEditorOpen(true);
  };

  const toggleDayInDraft = (idx) => {
    setDraft((d) => {
      const has = d.days_of_week.includes(idx);
      const next = has ? d.days_of_week.filter((x) => x !== idx) : [...d.days_of_week, idx].sort();
      return { ...d, days_of_week: next };
    });
  };

  const updateDraftZone = (i, patch) => {
    setDraft((d) => ({ ...d, zones: d.zones.map((z, idx) => (idx === i ? { ...z, ...patch } : z)) }));
  };

  const addZoneToDraft = () => {
    const used = new Set(draft.zones.map((z) => z.rachio_zone_id));
    const next = rachioZones.find((rz) => !used.has(rz.id)) || rachioZones[0];
    if (!next) {
      toast.error('Connect Rachio first to pick zones.');
      return;
    }
    setDraft((d) => ({
      ...d,
      zones: [...d.zones, {
        rachio_zone_id: next.id,
        duration_minutes: 10,
        sort_order: d.zones.length + 1,
        name: next.name,
        cycle_count: 1,
        cycle_minutes: null,
        soak_minutes: 0,
      }],
    }));
  };

  const removeZoneFromDraft = (i) => {
    setDraft((d) => ({ ...d, zones: d.zones.filter((_, idx) => idx !== i) }));
  };

  const save = async () => {
    if (!draft.name?.trim()) { toast.error('Schedule needs a name.'); return; }
    if (!draft.days_of_week.length) { toast.error('Pick at least one day.'); return; }
    if (!draft.zones.length) { toast.error('Add at least one zone.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        days_of_week: draft.days_of_week,
        start_time_local: draft.start_time_local,
        timezone: draft.timezone,
        zones: draft.zones.map((z, i) => ({
          rachio_zone_id: z.rachio_zone_id,
          // ⚠ Preserve explicit 0 — surgical "exclude this zone" affordance.
          // Number(0) is 0 which is falsy, so `|| 10` would silently coerce
          // it back to 10 and the user's "off" decision would be lost.
          duration_minutes: (() => {
            const n = Number(z.duration_minutes);
            return Number.isFinite(n) ? Math.max(0, Math.min(180, n)) : 10;
          })(),
          sort_order: i + 1,
          name: z.name,
          cycle_count: Number(z.cycle_count) || 1,
          cycle_minutes: z.cycle_minutes ? Number(z.cycle_minutes) : null,
          soak_minutes: Number(z.soak_minutes) || 0,
          soil_type: z.soil_type || null,
          slope: z.slope || null,
          nozzle_inhr: z.nozzle_inhr ? Number(z.nozzle_inhr) : null,
          root_depth_in: z.root_depth_in ? Number(z.root_depth_in) : null,
          area_sqft: z.area_sqft ? Number(z.area_sqft) : null,
          sun_exposure: z.sun_exposure || null,
          skip_if_zone_moisture_above_pct:
            z.skip_if_zone_moisture_above_pct === null
            || z.skip_if_zone_moisture_above_pct === undefined
            || z.skip_if_zone_moisture_above_pct === ''
              ? null
              : Number(z.skip_if_zone_moisture_above_pct),
        })),
        smart_skip: draft.smart_skip,
        split_strategy: draft.split_strategy,
        probe_driven_tuning: !!draft.probe_driven_tuning,
        adaptive_runtime: !!draft.adaptive_runtime,
        time_aware_optimization: !!draft.time_aware_optimization,
      };
      if (editingId) {
        await axios.put(`${API_URL}/api/irrigation/schedules/${editingId}`, { ...payload, enabled: draft.enabled }, { withCredentials: true });
        toast.success('Schedule updated.');
      } else {
        await axios.post(`${API_URL}/api/irrigation/schedules`, payload, { withCredentials: true });
        toast.success('Schedule created.');
      }
      setEditorOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────
  const formatNext = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  };

  return (
    <>
    <Card className="mb-4" data-testid="rachio-schedule-builder">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-500" />
              Schedules
            </CardTitle>
            <CardDescription>
              Build, run, and edit your watering schedules — every dial Rachio's app has, plus TurfPulse smart skip and smart-split.
            </CardDescription>
          </div>
          <Button
            onClick={openNew}
            disabled={!rachioConnected}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="schedule-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New schedule
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</div>
        ) : schedules.length === 0 && !rachioConnected ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="schedule-no-rachio">
            <Droplets className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Connect Rachio first to build schedules.</p>
            <a href="/rachio/connect" className="text-xs text-emerald-600 underline mt-1 inline-block">Connect Rachio →</a>
          </div>
        ) : schedules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="schedule-empty">
            <Clock className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No schedules yet. Build your first one.</p>
            <Button onClick={openNew} className="mt-3" size="sm" data-testid="schedule-empty-cta">
              <Plus className="w-4 h-4 mr-2" /> New schedule
            </Button>
          </div>
        ) : (
          <>
            {!rachioConnected && (
              <div
                className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs flex items-center justify-between gap-3"
                data-testid="schedule-rachio-warning"
              >
                <span className="text-amber-800 dark:text-amber-300">
                  ⚠ Rachio token not detected — schedules can be edited but <strong>Run-now and auto-fire are disabled</strong> until you reconnect.
                </span>
                <a href="/rachio/connect" className="text-amber-900 dark:text-amber-200 underline shrink-0">Reconnect →</a>
              </div>
            )}
            <div className="space-y-3">
            {schedules.map((s) => {
              const totalMin = (s.zones || []).reduce((acc, z) => {
                const dm = Number(z.duration_minutes) || 0;
                if (dm === 0) return acc; // excluded zone — don't inflate totals
                const cc = Number(z.cycle_count) || 1;
                const cm = z.cycle_minutes ? Number(z.cycle_minutes) : Math.max(1, Math.floor(dm / cc));
                return acc + cm * cc;
              }, 0);
              const isSmartSplit = s.split_strategy === 'smart_gap' && (s.days_of_week || []).length > 1;
              const slips = (s.days_of_week || []).length > 1
                ? computePerDaySchedule(s.days_of_week, s.zones, s.split_strategy)
                : null;
              return (
                <div
                  key={s.id}
                  className="border border-border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid={`schedule-row-${s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate" data-testid={`schedule-name-${s.id}`}>{s.name}</span>
                      {s.enabled ? (
                        <Badge className="text-[9px] uppercase bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">Active</Badge>
                      ) : (
                        <Badge className="text-[9px] uppercase bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30">Paused</Badge>
                      )}
                      {s.split_strategy === 'smart_gap' && (
                        <Badge className="text-[9px] uppercase bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-500/40">✨ Smart Split</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex gap-0.5">
                        {DOW.map((d) => (
                          <span key={d.idx} className={`text-[10px] w-5 h-5 flex items-center justify-center rounded ${
                            (s.days_of_week || []).includes(d.idx) ? 'bg-emerald-500 text-white font-bold' : 'bg-muted text-muted-foreground/50'
                          }`}>{d.key}</span>
                        ))}
                      </span>
                      <span>· {s.start_time_local}</span>
                      <span>· {s.zones?.length || 0} zone{s.zones?.length !== 1 ? 's' : ''}</span>
                      <span>· ~{totalMin} min</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1.5">
                      Next: {formatNext(s.next_fire_at)}
                      {s.last_skip_reason && <span className="text-amber-500"> · last skip: {s.last_skip_reason}</span>}
                      {s.fire_count > 0 && <span> · {s.fire_count} runs</span>}
                    </div>
                    {/* Jun 4 2026 — "Master fired 8 zones on wet soil" guardrail.
                        When probes are connected but this schedule has no moisture
                        gate set, surface the gap explicitly so the user can opt in. */}
                    {s.moisture_gate_warning && (
                      <div
                        data-testid={`schedule-no-moisture-gate-${s.id}`}
                        className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-2.5 py-2 text-[11px] text-amber-700 dark:text-amber-300 leading-snug"
                      >
                        <span className="font-semibold">⚠ Fires unconditionally — </span>
                        {s.moisture_gate_warning}
                      </div>
                    )}
                    {slips && slips.length > 1 && (
                      <div
                        className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-2 space-y-2"
                        data-testid={`schedule-split-breakdown-${s.id}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-300 text-[9px]">
                            {isSmartSplit ? '✨ Smart Split — per-day schedule slips' : 'Per-day schedule slips'}
                          </span>
                          <span className="text-[9px] text-muted-foreground">
                            {isSmartSplit ? 'weekly water unchanged · redistributed by gap' : 'identical runtime each day'}
                          </span>
                        </div>
                        <PerDayScheduleSlips slips={slips} compact testIdPrefix={`schedule-slip-${s.id}`} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    <Button size="sm" onClick={() => runNow(s)} disabled={busy[s.id] || !rachioConnected} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`schedule-run-${s.id}`}>
                      {busy[s.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Run</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => skipNext(s)} disabled={busy[s.id]} data-testid={`schedule-skip-${s.id}`}>
                      <SkipForward className="w-3.5 h-3.5 mr-1" /> Skip
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(s)} disabled={busy[s.id]} data-testid={`schedule-edit-${s.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => togglePause(s)} disabled={busy[s.id]} data-testid={`schedule-toggle-${s.id}`}>
                      {s.enabled ? 'Pause' : 'Resume'}
                    </Button>
                    <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600" onClick={() => remove(s)} disabled={busy[s.id]} data-testid={`schedule-delete-${s.id}`}>
                      <Square className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
            </div>
          </>
        )}
      </CardContent>

      {/* ── Editor modal ── */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="schedule-editor">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit schedule' : 'New schedule'}</DialogTitle>
            <DialogDescription>Same dials as Rachio's app, plus TurfPulse smart-skip and cycle-and-soak.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {/* Name */}
            <div>
              <Label>Name</Label>
              <Input
                data-testid="schedule-name-input"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="e.g., Front Yard Tue/Thu"
              />
            </div>

            {/* Days */}
            <div>
              <Label>Days</Label>
              <div className="flex gap-1.5 mt-1.5">
                {DOW.map((d) => {
                  const active = draft.days_of_week.includes(d.idx);
                  return (
                    <button
                      key={d.idx}
                      type="button"
                      onClick={() => toggleDayInDraft(d.idx)}
                      data-testid={`schedule-day-${d.idx}`}
                      className={`w-10 h-10 rounded-lg text-xs font-semibold transition-colors ${
                        active ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {d.key}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start time</Label>
                <Input
                  type="time"
                  data-testid="schedule-time-input"
                  value={draft.start_time_local}
                  onChange={(e) => setDraft({ ...draft, start_time_local: e.target.value })}
                />
              </div>
              <div>
                <Label>Split strategy</Label>
                <Select value={draft.split_strategy} onValueChange={(v) => setDraft({ ...draft, split_strategy: v })}>
                  <SelectTrigger data-testid="schedule-split-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="even">Even — same runtime each day</SelectItem>
                    <SelectItem value="smart_gap">✨ Smart Split — deeper before long dry gap</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Probe-Driven Duration Tuning toggle — Rung 3 autonomy */}
            <div
              className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 flex items-start gap-3"
              data-testid="schedule-probe-driven-toggle-row"
            >
              <Switch
                checked={!!draft.probe_driven_tuning}
                onCheckedChange={(v) => setDraft({ ...draft, probe_driven_tuning: v })}
                data-testid="schedule-probe-driven-toggle"
              />
              <div className="flex-1">
                <Label className="text-sm font-semibold text-sky-700 dark:text-sky-300">
                  ✨ Probe-Driven Duration Tuning
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-sky-600/80">Enthusiast</span>
                </Label>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  Replace your typed duration with a runtime computed from each zone's
                  current SVWC reading. Same total weekly water — distributed where the
                  soil asks for it. Falls back to typed duration when probe is unavailable.{' '}
                  <a
                    href="/methodology/probe-driven-irrigation"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sky-700 dark:text-sky-400 underline"
                    data-testid="schedule-probe-driven-methodology-link"
                  >
                    See the math →
                  </a>
                </p>
              </div>
            </div>

            {/* Live Smart Split preview — exact runtime per day, per zone */}
            {draft.split_strategy === 'smart_gap' && draft.days_of_week.length > 1 && draft.zones.length > 0 && (() => {
              const slips = computePerDaySchedule(draft.days_of_week, draft.zones, 'smart_gap');
              const weeklyTotal = slips.reduce((s, d) => s + d.total_minutes, 0);
              return (
                <div
                  className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2"
                  data-testid="schedule-split-preview"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-700 dark:text-emerald-300">
                      ✨ Smart Split preview · this is exactly what fires each day
                    </div>
                    <div className="text-[10px] font-mono tabular-nums text-muted-foreground">
                      Σ {weeklyTotal} min/wk
                    </div>
                  </div>
                  <PerDayScheduleSlips slips={slips} compact={false} testIdPrefix="schedule-split-preview" />
                  <div className="text-[10px] text-muted-foreground italic pt-1 border-t border-emerald-500/20">
                    Math: per-day multiplier = (gap-days / 7) × {draft.days_of_week.length} sessions. Each zone's per-cycle minutes × multiplier; cycles round-robined across zones for natural soak (same trick Rachio uses).
                  </div>
                </div>
              );
            })()}

            {/* Even-split preview — identical schedule each day */}
            {draft.split_strategy === 'even' && draft.days_of_week.length > 0 && draft.zones.length > 0 && (() => {
              const slips = computePerDaySchedule(draft.days_of_week, draft.zones, 'even');
              const weeklyTotal = slips.reduce((s, d) => s + d.total_minutes, 0);
              return (
                <div
                  className="rounded-lg border border-border bg-muted/30 p-3 space-y-2"
                  data-testid="schedule-even-preview"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                      Schedule preview · same runtime every day
                    </div>
                    <div className="text-[10px] font-mono tabular-nums text-muted-foreground">
                      Σ {weeklyTotal} min/wk
                    </div>
                  </div>
                  <PerDayScheduleSlips slips={slips} compact={false} testIdPrefix="schedule-even-preview" />
                </div>
              );
            })()}

            {/* Zones */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Zones</Label>
                <Button size="sm" variant="outline" onClick={addZoneToDraft} data-testid="schedule-add-zone-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add zone
                </Button>
              </div>
              <div className="space-y-2 mt-2">
                {draft.zones.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-4 border border-dashed rounded">No zones — add one above.</div>
                )}
                {draft.zones.map((z, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-2" data-testid={`schedule-zone-row-${i}`}>
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 sm:col-span-5">
                        <Label className="text-[10px]">Zone</Label>
                        <Select
                          value={z.rachio_zone_id}
                          onValueChange={(v) => {
                            const rz = rachioZones.find((rrz) => rrz.id === v);
                            updateDraftZone(i, { rachio_zone_id: v, name: rz?.name || z.name });
                          }}
                        >
                          <SelectTrigger data-testid={`schedule-zone-select-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {rachioZones.map((rz) => (
                              <SelectItem key={rz.id} value={rz.id}>
                                Zone {rz.zone_number} · {rz.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-4 sm:col-span-3">
                        <Label className="text-[10px]">Run time (min)</Label>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="h-8 w-8 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { duration_minutes: Math.max(0, (Number(z.duration_minutes) || 0) - 1) })}
                            data-testid={`schedule-zone-duration-dec-${i}`}
                            aria-label="Decrease run time"
                          >−</button>
                          <Input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min={0}
                            max={180}
                            step={1}
                            value={z.duration_minutes}
                            onChange={(e) => {
                              const raw = e.target.value;
                              // Allow explicit 0 — surgical "exclude this zone" affordance.
                              // parseInt('') returns NaN which we coerce to 0 for clean typing.
                              const n = raw === '' ? 0 : Math.max(0, Math.min(180, parseInt(raw, 10) || 0));
                              updateDraftZone(i, { duration_minutes: n });
                            }}
                            className="text-center"
                            data-testid={`schedule-zone-duration-${i}`}
                          />
                          <button
                            type="button"
                            className="h-8 w-8 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { duration_minutes: Math.min(180, (Number(z.duration_minutes) || 0) + 1) })}
                            data-testid={`schedule-zone-duration-inc-${i}`}
                            aria-label="Increase run time"
                          >+</button>
                        </div>
                        {z.duration_minutes === 0 && (
                          <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5" data-testid={`schedule-zone-excluded-${i}`}>
                            ⊘ Excluded — won't fire
                          </div>
                        )}
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        <Label className="text-[10px]">Cycles</Label>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="h-8 w-8 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { cycle_count: Math.max(1, (Number(z.cycle_count) || 1) - 1) })}
                            data-testid={`schedule-zone-cycles-dec-${i}`}
                            aria-label="Decrease cycles"
                          >−</button>
                          <Input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min={1}
                            max={6}
                            step={1}
                            value={z.cycle_count}
                            onChange={(e) => updateDraftZone(i, { cycle_count: parseInt(e.target.value, 10) || 1 })}
                            className="text-center"
                            data-testid={`schedule-zone-cycles-${i}`}
                          />
                          <button
                            type="button"
                            className="h-8 w-8 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { cycle_count: Math.min(6, (Number(z.cycle_count) || 1) + 1) })}
                            data-testid={`schedule-zone-cycles-inc-${i}`}
                            aria-label="Increase cycles"
                          >+</button>
                        </div>
                      </div>
                      <div className="col-span-4 sm:col-span-2 text-right">
                        <Button size="sm" variant="ghost" className="text-red-500 mt-4" onClick={() => removeZoneFromDraft(i)} data-testid={`schedule-zone-remove-${i}`}>
                          <Square className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {/* Per-zone moisture gate (Jun 4 followup) — closed-loop trigger:
                        "fire this zone only if its mapped probe reads BELOW threshold". */}
                    {z.duration_minutes > 0 && (
                      <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-300/40 dark:border-emerald-800/50 rounded p-2 space-y-1" data-testid={`schedule-zone-moisture-gate-${i}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400 shrink-0">
                            Hold zone if probe ≥
                          </Label>
                          <Input
                            type="number" inputMode="numeric" min={0} max={100} step={1}
                            value={z.skip_if_zone_moisture_above_pct ?? ''}
                            placeholder="off"
                            onChange={(e) => {
                              const raw = e.target.value;
                              const v = raw === '' ? null : Math.max(0, Math.min(100, parseFloat(raw) || 0));
                              updateDraftZone(i, { skip_if_zone_moisture_above_pct: v });
                            }}
                            className="text-center h-7 w-16 text-xs"
                            data-testid={`schedule-zone-moisture-threshold-${i}`}
                          />
                          <span className="text-[11px] text-muted-foreground">% VWC</span>
                        </div>
                        <div className="text-[10px] text-emerald-700 dark:text-emerald-400/80 leading-snug">
                          Closed-loop: TurfPulse holds this zone out of the fire when its mapped Ecowitt probe reads above the threshold. Leave empty to use the schedule-wide gate. Common values: <strong>45%</strong> (loam) · <strong>40%</strong> (sandy) · <strong>50%</strong> (clay).
                        </div>
                      </div>
                    )}
                    {z.cycle_count > 1 && z.duration_minutes > 0 && (
                      <div className="text-[11px] text-muted-foreground bg-muted/30 rounded p-2 space-y-2" data-testid={`schedule-zone-soak-row-${i}`}>
                        <div>
                          ✓ Cycle-and-soak: <strong>{z.cycle_count} cycles × ~{Math.max(1, Math.floor(z.duration_minutes / z.cycle_count))} min each</strong>
                          {(Number(z.soak_minutes) || 0) > 0
                            ? <> · <strong>{z.soak_minutes}m soak</strong> requested between cycles</>
                            : <> · soak via round-robin (other zones run while this one rests)</>}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <Label className="text-[10px] uppercase tracking-wider text-muted-foreground shrink-0">Soak between cycles (min)</Label>
                          <button
                            type="button"
                            className="h-7 w-7 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { soak_minutes: Math.max(0, (Number(z.soak_minutes) || 0) - 5) })}
                            data-testid={`schedule-zone-soak-dec-${i}`}
                            aria-label="Decrease soak time"
                          >−</button>
                          <Input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min={0}
                            max={120}
                            step={5}
                            value={Number(z.soak_minutes) || 0}
                            onChange={(e) => {
                              const raw = e.target.value;
                              const n = raw === '' ? 0 : Math.max(0, Math.min(120, parseInt(raw, 10) || 0));
                              updateDraftZone(i, { soak_minutes: n });
                            }}
                            className="text-center h-7 w-16 text-xs"
                            data-testid={`schedule-zone-soak-${i}`}
                          />
                          <button
                            type="button"
                            className="h-7 w-7 rounded border bg-background hover:bg-muted text-sm font-bold shrink-0"
                            onClick={() => updateDraftZone(i, { soak_minutes: Math.min(120, (Number(z.soak_minutes) || 0) + 5) })}
                            data-testid={`schedule-zone-soak-inc-${i}`}
                            aria-label="Increase soak time"
                          >+</button>
                        </div>
                        <div className="text-[10px] italic text-emerald-700 dark:text-emerald-400">
                          ✅ <strong>Live in v1.1:</strong> the auto-fire engine now honors this explicit pause between cycles. The cron fires round 1, waits this many minutes, then fires round 2. Set 0 to keep the default round-robin behavior.
                        </div>
                      </div>
                    )}
                    {/* Advanced per-zone settings (drives Water Budget runtime math) */}
                    <button
                      type="button"
                      className="text-[11px] text-emerald-600 dark:text-emerald-400 hover:underline"
                      onClick={() => updateDraftZone(i, { _showAdvanced: !z._showAdvanced })}
                      data-testid={`schedule-zone-advanced-toggle-${i}`}
                    >
                      {z._showAdvanced ? '▾ Hide advanced settings' : '▸ Advanced settings (soil, slope, nozzle…)'}
                    </button>
                    {z._showAdvanced && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-2 border-t" data-testid={`schedule-zone-advanced-${i}`}>
                        <div>
                          <Label className="text-[10px]">Soil type</Label>
                          <Select value={z.soil_type || ''} onValueChange={(v) => updateDraftZone(i, { soil_type: v || null })}>
                            <SelectTrigger data-testid={`schedule-zone-soil-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sandy">Sandy</SelectItem>
                              <SelectItem value="sandy_loam">Sandy loam</SelectItem>
                              <SelectItem value="loam">Loam</SelectItem>
                              <SelectItem value="silty_loam">Silty loam</SelectItem>
                              <SelectItem value="silty_clay">Silty clay</SelectItem>
                              <SelectItem value="clay">Clay</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Slope</Label>
                          <Select value={z.slope || ''} onValueChange={(v) => updateDraftZone(i, { slope: v || null })}>
                            <SelectTrigger data-testid={`schedule-zone-slope-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Flat</SelectItem>
                              <SelectItem value="gentle">Gentle</SelectItem>
                              <SelectItem value="moderate">Moderate</SelectItem>
                              <SelectItem value="steep">Steep</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Sun exposure</Label>
                          <Select value={z.sun_exposure || ''} onValueChange={(v) => updateDraftZone(i, { sun_exposure: v || null })}>
                            <SelectTrigger data-testid={`schedule-zone-sun-${i}`}><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full_sun">Full sun</SelectItem>
                              <SelectItem value="part_sun">Part sun</SelectItem>
                              <SelectItem value="shade">Shade</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-[10px]">Nozzle (in/hr)</Label>
                          <Input
                            type="number" step="0.1" min="0.1" max="4"
                            value={z.nozzle_inhr ?? ''}
                            onChange={(e) => updateDraftZone(i, { nozzle_inhr: e.target.value === '' ? null : parseFloat(e.target.value) })}
                            data-testid={`schedule-zone-nozzle-${i}`}
                            placeholder="1.5"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Root depth (in)</Label>
                          <Input
                            type="number" min="1" max="24"
                            value={z.root_depth_in ?? ''}
                            onChange={(e) => updateDraftZone(i, { root_depth_in: e.target.value === '' ? null : parseFloat(e.target.value) })}
                            data-testid={`schedule-zone-root-${i}`}
                            placeholder="6"
                          />
                        </div>
                        <div>
                          <Label className="text-[10px]">Area (sq ft)</Label>
                          <Input
                            type="number" min="10" max="100000"
                            value={z.area_sqft ?? ''}
                            onChange={(e) => updateDraftZone(i, { area_sqft: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                            data-testid={`schedule-zone-area-${i}`}
                            placeholder="1000"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Decision Engine v2.0 toggles — Rule 13 + Adaptive + Time-Aware.
                These three toggles surface the entire backend Decision Engine
                upgrade. Defaults are OFF so existing schedules behave exactly
                the same; users opt in when they're ready. */}
            <div className="space-y-2" data-testid="schedule-engine-v2-toggles">
              <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 font-semibold pl-1">
                ✨ Decision Engine v2.0 · Closed-loop Intelligence
              </div>

              {/* Rule 13 — Forecast Intelligence */}
              <div
                className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 flex items-start gap-3"
                data-testid="schedule-rule13-toggle-row"
              >
                <Switch
                  checked={!!draft.smart_skip.skip_if_forecast_intelligence_says_skip}
                  onCheckedChange={(v) => setDraft({
                    ...draft,
                    smart_skip: { ...draft.smart_skip, skip_if_forecast_intelligence_says_skip: v },
                  })}
                  data-testid="schedule-rule13-toggle"
                />
                <div className="flex-1">
                  <Label className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    🌦 Rule 13 — Forecast Intelligence
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-600/80">Beta</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Skip when Open-Meteo's 48h forecast scores below threshold (rain
                    coming, oppressive heat, dewpoint disease tier). Trained against
                    your verified-fire history — the engine learns YOUR forecast accuracy.
                  </p>
                </div>
              </div>

              {/* Adaptive Runtime */}
              <div
                className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-3"
                data-testid="schedule-adaptive-runtime-toggle-row"
              >
                <Switch
                  checked={!!draft.adaptive_runtime}
                  onCheckedChange={(v) => setDraft({ ...draft, adaptive_runtime: v })}
                  data-testid="schedule-adaptive-runtime-toggle"
                />
                <div className="flex-1">
                  <Label className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                    🎯 Adaptive Runtime Tapering
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-600/80">Enthusiast</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Reads live soil moisture before each fire. Drops runtime when soil's
                    already near MAX target; bumps it when below MIN. Same weekly water,
                    delivered only where the soil asks for it. Falls back to typed duration
                    when probe is offline.
                  </p>
                </div>
              </div>

              {/* Time-Aware Optimization */}
              <div
                className="rounded-lg border border-purple-500/30 bg-purple-500/5 p-3 flex items-start gap-3"
                data-testid="schedule-time-aware-toggle-row"
              >
                <Switch
                  checked={!!draft.time_aware_optimization}
                  onCheckedChange={(v) => setDraft({ ...draft, time_aware_optimization: v })}
                  data-testid="schedule-time-aware-toggle"
                />
                <div className="flex-1">
                  <Label className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                    ⏱ Time-Aware Window Optimization
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-purple-600/80">Pro</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    Postpones (up to 18h) when current VPD / solar / wind would burn the
                    water off as evaporation. The cron polls every 5 min — the moment an
                    IDEAL window opens, it fires. Best paired with Rule 13.
                  </p>
                </div>
              </div>
            </div>

            {/* TurfPulse Weather Intelligence — every gate shows the math */}
            <div className="border-t pt-4 space-y-3">
              <div>
                <Label className="text-base font-semibold">TurfPulse Weather Intelligence</Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Each gate runs before every fire and skips if it trips. Beats Rachio's WI Plus — every decision shows its math.
                </p>
              </div>

              {/* Rain skip */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-rain-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>🌧️ Rain skip</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if forecast 24h rain ≥ X in</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" step="0.05" min="0" max="2"
                    value={draft.smart_skip.skip_if_rain_forecast_in}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_rain_forecast_in: parseFloat(e.target.value) || 0 } })}
                    placeholder="0 = off"
                    data-testid="wi-rain-threshold"
                  />
                </div>
              </div>

              {/* Blackout skip */}
              <div className="flex items-center gap-3" data-testid="wi-blackout-row">
                <Switch
                  checked={!!draft.smart_skip.skip_if_in_blackout}
                  onCheckedChange={(v) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_in_blackout: v } })}
                  data-testid="wi-blackout-toggle"
                />
                <Label className="text-xs">
                  🚫 City blackout skip
                  <span className="text-muted-foreground/70 ml-1">— skip during 10am–7pm watering ban</span>
                </Label>
              </div>

              {/* Freeze skip */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-freeze-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>❄️ Freeze skip</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if min temp 12h ≤ °F (recommend 37)</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" min="0" max="50"
                    value={draft.smart_skip.skip_if_freeze_below_f ?? ''}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_freeze_below_f: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                    placeholder="off — leave blank to disable"
                    data-testid="wi-freeze-threshold"
                  />
                </div>
              </div>

              {/* Wind skip */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-wind-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>💨 Wind drift skip</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if peak wind 6h ≥ mph (recommend 15)</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" min="0" max="100"
                    value={draft.smart_skip.skip_if_wind_above_mph ?? ''}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_wind_above_mph: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                    placeholder="off — leave blank to disable"
                    data-testid="wi-wind-threshold"
                  />
                </div>
              </div>

              {/* Saturation skip (Ecowitt WET probe) */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-saturation-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>💧 Saturation skip</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if Ecowitt WET probe ≥ % (recommend 80)</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" min="0" max="100"
                    value={draft.smart_skip.skip_if_soil_moisture_above_pct ?? ''}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_soil_moisture_above_pct: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                    placeholder="off — leave blank to disable"
                    data-testid="wi-saturation-threshold"
                  />
                </div>
              </div>

              {/* MAD bypass */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-mad-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>🪣 MAD bypass</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if depletion below % (lawn not dry enough)</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" min="0" max="100"
                    value={draft.smart_skip.skip_if_mad_below_pct ?? ''}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_mad_below_pct: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                    placeholder="off — leave blank to disable"
                    data-testid="wi-mad-threshold"
                  />
                </div>
              </div>

              {/* Dewpoint lock — disease pressure */}
              <div className="grid grid-cols-12 gap-3 items-end" data-testid="wi-dewpoint-row">
                <div className="col-span-12 sm:col-span-7">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>🦠 Dewpoint lock</span>
                    <span className="text-muted-foreground/70 text-[10px]">— skip if dewpoint stuck ≥ °F (recommend 65 = disease tier)</span>
                  </Label>
                </div>
                <div className="col-span-12 sm:col-span-5">
                  <Input
                    type="number" min="40" max="100"
                    value={draft.smart_skip.skip_if_dewpoint_above_f ?? ''}
                    onChange={(e) => setDraft({ ...draft, smart_skip: { ...draft.smart_skip, skip_if_dewpoint_above_f: e.target.value === '' ? null : parseFloat(e.target.value) } })}
                    placeholder="off — leave blank to disable"
                    data-testid="wi-dewpoint-threshold"
                  />
                </div>
              </div>
            </div>

            {editingId && (
              <div className="flex items-center gap-3 border-t pt-4">
                <Switch
                  checked={!!draft.enabled}
                  onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
                  data-testid="schedule-enabled-toggle"
                />
                <Label className="text-xs">{draft.enabled ? 'Active' : 'Paused'}</Label>
              </div>
            )}

            <div className="flex gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setEditorOpen(false)} disabled={saving} className="flex-1" data-testid="schedule-cancel-btn">
                Cancel
              </Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="schedule-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingId ? 'Save changes' : 'Create schedule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>

    {/* ── Activity log — every TurfPulse fire + Rachio webhook event ── */}
    <Card className="mb-8" data-testid="schedule-activity-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-emerald-500" /> Activity
        </CardTitle>
        <CardDescription>
          Every shot fired — TurfPulse cron, manual Run-now, and Rachio webhook events. Honest paper trail.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Per-zone summary (last 7 days) */}
        {byZone.length > 0 && (
          <div className="mb-4 pb-4 border-b" data-testid="activity-by-zone">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Last 7 days · by zone
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {byZone.map((z) => (
                <div
                  key={z.zone_id}
                  className="rounded-lg border border-border/50 bg-emerald-500/[0.03] p-2.5"
                  data-testid={`activity-zone-${z.zone_id}`}
                >
                  <div className="text-xs font-semibold text-foreground truncate">{z.zone_name}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Ran <strong className="text-foreground">{z.run_count}×</strong>
                    {' · '}
                    <strong className="text-foreground">{z.total_minutes} min</strong>
                    {z.total_inches > 0 && (
                      <>
                        {' · '}
                        <strong className="text-emerald-600 dark:text-emerald-400">{z.total_inches}″</strong>
                        <span className="text-muted-foreground/70"> delivered</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Linear feed */}
        {!activityLoaded ? (
          <div className="text-center py-4 text-muted-foreground text-xs"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        ) : activity.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-xs" data-testid="activity-empty">
            No runs logged yet. Once Rachio fires (manually, scheduled, or via webhook), runs will appear here.
          </div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {activity.map((r, i) => {
              const when = r.fired_at ? new Date(r.fired_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
              const isSkip = r.status === 'skipped' || r.skip_reason;
              const isFire = r.status === 'fired' || r.status === 'ok' || r.zones_count;
              const isGhost = r.status === 'tier_blocked' && (r.ghost_zones_count || r.ghost_zones);
              const snap = r.agronomic_snapshot;
              const hasReceipt = snap && (snap.mad || snap.weather || snap.soil || snap.last_rain);
              const isOpen = expandedRun === i;
              return (
                <div key={i} className="border-b border-border/30 last:border-0 pb-2" data-testid={`activity-row-${i}`}>
                  <button
                    type="button"
                    className={`w-full flex items-start gap-3 text-xs text-left ${hasReceipt || isGhost ? 'hover:bg-muted/30 rounded px-1 py-1 -mx-1 cursor-pointer' : ''}`}
                    onClick={() => (hasReceipt || isGhost) && setExpandedRun(isOpen ? null : i)}
                    data-testid={`activity-row-toggle-${i}`}
                  >
                    <span className={`shrink-0 mt-0.5 w-2 h-2 rounded-full ${
                      isGhost ? 'bg-amber-400 ring-2 ring-amber-200 dark:ring-amber-900' :
                      isSkip ? 'bg-amber-500' : isFire ? 'bg-emerald-500' : 'bg-muted-foreground/40'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground">
                        {isGhost ? (
                          <span className="italic text-amber-700 dark:text-amber-300" data-testid={`activity-ghost-${i}`}>
                            👻 Would have fired {r.ghost_zones_count || 0} zone{(r.ghost_zones_count || 0) !== 1 ? 's' : ''}
                            {r.ghost_total_seconds ? ` for ${Math.round(r.ghost_total_seconds / 60)} min` : ''}
                            <span className="ml-2 text-[10px] not-italic font-normal opacity-75">· advisory mode</span>
                          </span>
                        ) : (
                          <>
                            {r.zone_name && <>Zone {r.zone_name} · </>}
                            {r.duration_seconds && <>{Math.round(r.duration_seconds / 60)} min</>}
                            {r.delivered_inches != null && <> · <span className="text-emerald-600 dark:text-emerald-400">{r.delivered_inches}″</span></>}
                            {r.zones_count && <>{r.zones_count} zone{r.zones_count !== 1 ? 's' : ''} fired</>}
                            {isSkip && <span className="text-amber-600 dark:text-amber-400">⚠ Skipped</span>}
                          </>
                        )}
                      </div>
                      {(r.skip_reason || r.reason) && !isGhost && (
                        <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">↳ {r.skip_reason || r.reason}</div>
                      )}
                      {isGhost && (
                        <div className="text-[10px] text-amber-700/80 dark:text-amber-300/80 mt-0.5">
                          ↳ Upgrade to Inner Circle to unlock auto-fire on this schedule.
                        </div>
                      )}
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {when}
                        {r.source && <> · {String(r.source).replace(/_/g, ' ')}</>}
                        {r.topic && <> · {r.topic}</>}
                        {hasReceipt && (
                          <span className="ml-2 text-emerald-600 dark:text-emerald-400 font-medium">
                            {isOpen ? '▾ hide receipt' : '▸ tap for receipt'}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>

                  {isOpen && hasReceipt && (
                    <div
                      className="ml-5 mt-2 rounded-lg border border-border/60 bg-muted/30 p-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[11px]"
                      data-testid={`activity-receipt-${i}`}
                    >
                      {/* MAD bucket */}
                      {snap.mad && (
                        <div>
                          <div className="uppercase tracking-widest text-muted-foreground font-semibold mb-1 flex items-center gap-2 flex-wrap">
                            <span>🪣 MAD bucket</span>
                            <MadSourcePill mad={snap.mad} size="xs" />
                          </div>
                          <div className="space-y-0.5">
                            {snap.mad.depletion_pct != null && (
                              <div className="flex justify-between">
                                <span>Depletion</span>
                                <span className={`font-mono tabular-nums font-semibold ${
                                  snap.mad.depletion_pct >= (snap.mad.threshold_pct ?? 50) ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
                                }`}>{Math.round(snap.mad.depletion_pct)}%</span>
                              </div>
                            )}
                            {snap.mad.bucket_inches != null && (
                              <div className="flex justify-between">
                                <span>Bucket size</span>
                                <span className="font-mono tabular-nums">{Number(snap.mad.bucket_inches).toFixed(2)}″</span>
                              </div>
                            )}
                            {snap.mad.deficit_inches != null && (
                              <div className="flex justify-between">
                                <span>Deficit to refill</span>
                                <span className="font-mono tabular-nums">{Number(snap.mad.deficit_inches).toFixed(2)}″</span>
                              </div>
                            )}
                            {snap.mad.threshold_pct != null && (
                              <div className="flex justify-between text-muted-foreground">
                                <span>Trigger threshold</span>
                                <span className="font-mono tabular-nums">{Math.round(snap.mad.threshold_pct)}%</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Weather */}
                      {snap.weather && (
                        <div>
                          <div className="uppercase tracking-widest text-muted-foreground font-semibold mb-1">🌦 Weather (next 12h)</div>
                          <div className="space-y-0.5">
                            {snap.weather.rain_forecast_24h_in != null && (
                              <div className="flex justify-between">
                                <span>Rain forecast 24h</span>
                                <span className="font-mono tabular-nums">{Number(snap.weather.rain_forecast_24h_in).toFixed(2)}″</span>
                              </div>
                            )}
                            {snap.weather.min_temp_12h_f != null && (
                              <div className="flex justify-between">
                                <span>Min temp</span>
                                <span className="font-mono tabular-nums">{Math.round(snap.weather.min_temp_12h_f)}°F</span>
                              </div>
                            )}
                            {snap.weather.max_wind_6h_mph != null && (
                              <div className="flex justify-between">
                                <span>Peak wind 6h</span>
                                <span className="font-mono tabular-nums">{Math.round(snap.weather.max_wind_6h_mph)} mph</span>
                              </div>
                            )}
                            {snap.weather.max_dewpoint_12h_f != null && (
                              <div className="flex justify-between">
                                <span>Max dewpoint</span>
                                <span className={`font-mono tabular-nums ${
                                  snap.weather.max_dewpoint_12h_f >= 65 ? 'text-rose-600 dark:text-rose-400' : ''
                                }`}>{Math.round(snap.weather.max_dewpoint_12h_f)}°F</span>
                              </div>
                            )}
                            {snap.weather.dewpoint_locked_hours != null && snap.weather.dewpoint_locked_hours > 0 && (
                              <div className="flex justify-between text-muted-foreground">
                                <span>Dewpoint locked</span>
                                <span className="font-mono tabular-nums">{snap.weather.dewpoint_locked_hours} h ≥ 65°F</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Last rain */}
                      {snap.last_rain && (
                        <div>
                          <div className="uppercase tracking-widest text-muted-foreground font-semibold mb-1">🌧️ Last rain</div>
                          <div className="space-y-0.5">
                            {snap.last_rain.today_in != null && (
                              <div className="flex justify-between">
                                <span>Today</span>
                                <span className="font-mono tabular-nums">{Number(snap.last_rain.today_in).toFixed(2)}″</span>
                              </div>
                            )}
                            {snap.last_rain.yesterday_in != null && (
                              <div className="flex justify-between">
                                <span>Yesterday</span>
                                <span className="font-mono tabular-nums">{Number(snap.last_rain.yesterday_in).toFixed(2)}″</span>
                              </div>
                            )}
                            {snap.last_rain.weekly_in != null && (
                              <div className="flex justify-between text-muted-foreground">
                                <span>This week</span>
                                <span className="font-mono tabular-nums">{Number(snap.last_rain.weekly_in).toFixed(2)}″</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Soil moisture trio */}
                      {snap.soil && (
                        <div>
                          <div className="uppercase tracking-widest text-muted-foreground font-semibold mb-1">💧 Soil moisture</div>
                          <div className="space-y-0.5">
                            {['dry', 'avg', 'wet'].map((role) => {
                              const v = snap.soil[role];
                              if (!v) return null;
                              const pct = v.moisture_pct;
                              const tone = role === 'dry' ? 'text-rose-600 dark:text-rose-400' : role === 'wet' ? 'text-blue-600 dark:text-blue-400' : 'text-amber-600 dark:text-amber-400';
                              return (
                                <div key={role} className="flex justify-between">
                                  <span className="uppercase tracking-wider text-[10px]">{role} <span className="text-muted-foreground/70">ch{v.channel}</span></span>
                                  <span className={`font-mono tabular-nums ${tone} font-semibold`}>{Math.round(pct)}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      <div className="col-span-full text-[10px] text-muted-foreground/70 italic">
                        Snapshot at {snap.captured_at ? new Date(snap.captured_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'fire time'} · auditable receipt vs Rachio's WI Plus black box
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
    </>
  );
};

export default RachioScheduleBuilder;
